import { Link, NavLink, Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getPlatformStatus } from '@/api/auth';
import { leaveSession } from '@/auth/store';
import { logout as apiLogout } from '@/api/auth';
import ErrorBoundary from '@/components/ErrorBoundary';
import TwoFactorGate from '@/components/security/TwoFactorGate';
import {
  PLATFORM_SECTION_GROUPS,
  DEFAULT_PLATFORM_SECTION,
} from '@/pages/platform/platformSections';

/**
 * Platform Admin Блок A — окремий layout для super-admin розділу /platform.
 *
 * Відмінне від AppLayout оформлення (темна шапка «⚙ Platform Admin» +
 * попередження про вплив на ВСІХ клієнтів), щоб платформенний власник
 * чітко усвідомлював, що працює з глобальними системними даними, а не з
 * даними окремого tenant'а.
 *
 * Гейт: при монтуванні читає GET /auth/platform-status. Якщо user НЕ
 * платформенний власник — redirect на /. Поки статус завантажується —
 * показуємо нейтральний placeholder (не блимаємо контентом).
 */
export default function PlatformLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['platform-status'],
    queryFn: getPlatformStatus,
    retry: false,
  });

  // Активний розділ системних даних — з query-параметра ?tab=.
  const onSystemData = location.pathname.startsWith('/platform/system-data');
  const activeTab = (onSystemData && searchParams.get('tab')) || DEFAULT_PLATFORM_SECTION;

  async function handleLogout() {
    try { await apiLogout(); } catch { /* вихід однаково */ }
    leaveSession();   // ч.1229: вихід — з повним перезавантаженням сторінки
  }

  // Поки чекаємо відповідь — нейтральний екран.
  if (status.isLoading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh', color: '#94a3b8' }}>
        Завантаження…
      </div>
    );
  }

  // ч.1015: сюди заходять ДВІ різні ролі.
  const isOwner = !!status.data?.is_platform_owner;
  const isSupport = !!status.data?.is_support_operator;
  // ч.1319: редактор відео-інструкцій — лише розділ відео (ролики, зауваження, YouTube).
  const isVideo = !!status.data?.is_video_editor;
  if (!isOwner && !isSupport && !isVideo) {
    navigate('/', { replace: true });
    return null;
  }
  // Оператор підтримки працює ЛИШЕ зі зверненнями, редактор відео — лише з роликами:
  // системні дані платформи (валюти, податкові константи) — не їхня зона.
  // `/platform/support` і `/platform/support-analytics` — обидва
  // починаються з префікса, тож одна перевірка покриває обидва.
  const onSupport = location.pathname.startsWith('/platform/support');
  const onVideo = location.pathname.startsWith('/platform/video');
  if (!isOwner && !((isSupport && onSupport) || (isVideo && onVideo))) {
    navigate(isSupport ? '/platform/support' : '/platform/video', { replace: true });
    return null;
  }

  // ч.1124: панель відкривається лише з входу, підтвердженого кодом
  // двофакторного входу. Раніше про це знав тільки сервер: панель малювалась,
  // її перший запит отримував відмову, і людину мовчки перекидало на головну —
  // власник платформи без двофакторного входу не міг зайти й не розумів чому.
  // Тепер підтвердження (або налаштування з нуля) — прямо тут, до будь-якого
  // запиту панелі.
  if (status.data?.second_step_required) {
    return (
      <div style={{ maxWidth: '36rem', margin: '8vh auto', padding: '0 1rem' }}>
        <div className="card">
          <h1 style={{ marginTop: 0, fontSize: '1.3rem' }}>Панель платформи</h1>
          <p style={{ fontSize: '0.9rem' }}>
            Панель платформи відкривається лише після входу, підтвердженого кодом
            двофакторного входу: тут дані, що впливають на всіх клієнтів.
          </p>
          <TwoFactorGate onDone={() => { queryClient.invalidateQueries(); }} />
          <p style={{ margin: '1rem 0 0', fontSize: '0.85rem', display: 'flex', gap: '1rem' }}>
            <Link to="/">назад у кабінет</Link>
            <button type="button" className="btn-link" onClick={handleLogout}>вийти</button>
          </p>
        </div>
      </div>
    );
  }

  return (
    // ч.1006: власна сітка платформи — див. `.app--platform` у styles.css.
    <div className="app app--platform">
      <header
        className="app__header"
        style={{ background: 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)', color: '#fff' }}
      >
        <h1 style={{ color: '#fff' }}>⚙ Platform Admin</h1>
        <div className="right">
          {/* ч.1319: попередження — лише власнику; підтримка і редактор відео системних даних не змінюють. */}
          {isOwner && (
            <span
              style={{
                background: 'rgba(255,255,255,0.18)',
                padding: '0.25rem 0.6rem',
                borderRadius: '6px',
                fontSize: '0.8rem',
              }}
            >
              ⚠ Глобальні системні дані — впливає на ВСІХ клієнтів
            </span>
          )}
          <button onClick={handleLogout}>Вихід</button>
        </div>
      </header>
      <aside className="app__sidebar">
        {/* ч.1015: консоль підтримки — ОКРЕМИЙ пункт, а не вкладка
            системних даних: це інший рід роботи (черга звернень), і
            саме сюди щодня заходить служба підтримки. */}
        {(isOwner || isSupport) && (
          <>
            <div className="section">Підтримка</div>
            <NavLink to="/platform/support"
              style={({ isActive }) => (isActive
                ? { background: 'rgba(109,40,217,0.12)', fontWeight: 600 }
                : undefined)}>
              Звернення користувачів
            </NavLink>
            <NavLink to="/platform/support-analytics"
              style={({ isActive }) => (isActive
                ? { background: 'rgba(109,40,217,0.12)', fontWeight: 600 }
                : undefined)}>
              Аналітика
            </NavLink>
            {/* ч.1334: статті й питання-відповіді довідки — правка і доручення агенту. */}
            <NavLink to="/platform/support-help"
              style={({ isActive }) => (isActive
                ? { background: 'rgba(109,40,217,0.12)', fontWeight: 600 }
                : undefined)}>
              Статті довідки
            </NavLink>
          </>
        )}
        {/* ч.1319: відео-інструкції — власнику і редактору відео. */}
        {(isOwner || isVideo) && (
          <>
            <div className="section">Відео-інструкції</div>
            <NavLink to="/platform/video"
              style={({ isActive }) => (isActive
                ? { background: 'rgba(109,40,217,0.12)', fontWeight: 600 }
                : undefined)}>
              Ролики довідки
            </NavLink>
          </>
        )}

        {isOwner && PLATFORM_SECTION_GROUPS.map((group) => (
          <div key={group.title}>
            <div className="section">{group.title}</div>
            {group.sections.map((s) => (
              <Link
                key={s.id}
                to={`/platform/system-data?tab=${s.id}`}
                className={
                  onSystemData && activeTab === s.id ? 'active' : undefined
                }
                style={
                  onSystemData && activeTab === s.id
                    ? { background: 'rgba(109,40,217,0.12)', fontWeight: 600 }
                    : undefined
                }
              >
                {s.label}
              </Link>
            ))}
          </div>
        ))}
        {/* ч.1125: хто має доступ до панелі — лише власнику платформи. */}
        {isOwner && (
          <>
            <div className="section">Доступ</div>
            <NavLink to="/platform/access"
              style={({ isActive }) => (isActive
                ? { background: 'rgba(109,40,217,0.12)', fontWeight: 600 }
                : undefined)}>
              Доступ до платформи
            </NavLink>
            <NavLink to="/platform/testers"
              style={({ isActive }) => (isActive
                ? { background: 'rgba(109,40,217,0.12)', fontWeight: 600 }
                : undefined)}>
              Тестувальники
            </NavLink>
          </>
        )}
        <NavLink to="/" style={{ marginTop: '1.25rem', opacity: 0.8 }}>
          ← Назад у кабінет
        </NavLink>
      </aside>
      <main className="app__main">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}
